/**
 * Client Logo Service
 * Handles fetching and caching client logos from the Blob API
 */

const CLIENT_LOGO_STORAGE_KEY_PREFIX = 'clientLogo_';
const CLIENT_LOGO_EXPIRATION_KEY_PREFIX = 'clientLogoExpiration_';

/**
 * Get the storage key for a specific client logo
 */
const getClientLogoKey = (clientID: string): string => {
  return `${CLIENT_LOGO_STORAGE_KEY_PREFIX}${clientID}`;
};

/**
 * Get the storage key for a specific client logo expiration
 */
const getClientLogoExpirationKey = (clientID: string): string => {
  return `${CLIENT_LOGO_EXPIRATION_KEY_PREFIX}${clientID}`;
};

/**
 * Calculate expiration date (1 week from now)
 */
const getExpirationDate = (): Date => {
  const now = new Date();
  const oneWeekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days in milliseconds
  return oneWeekFromNow;
};

/**
 * Check if cached logo is still valid
 */
const isCacheValid = (clientID: string): boolean => {
  try {
    const expirationKey = getClientLogoExpirationKey(clientID);
    const expirationDateStr = localStorage.getItem(expirationKey);
    
    if (!expirationDateStr) {
      return false;
    }

    const expirationDate = new Date(expirationDateStr);
    const now = new Date();

    return now < expirationDate;
  } catch (error) {
    console.error('Error checking cache validity:', error);
    return false;
  }
};

/**
 * Get cached client logo
 */
export const getCachedClientLogo = (clientID: string): string | null => {
  try {
    if (!isCacheValid(clientID)) {
      // Clear expired cache
      clearClientLogoCache(clientID);
      return null;
    }

    const logoKey = getClientLogoKey(clientID);
    const logo = localStorage.getItem(logoKey);
    
    if (logo) {
      console.log(`Using cached logo for client: ${clientID}`);
      return logo;
    }

    return null;
  } catch (error) {
    console.error('Error reading cached client logo:', error);
    return null;
  }
};

/**
 * Store client logo in cache with expiration
 */
const storeCachedClientLogo = (clientID: string, logoData: string): void => {
  try {
    const logoKey = getClientLogoKey(clientID);
    const expirationKey = getClientLogoExpirationKey(clientID);
    const expirationDate = getExpirationDate();

    localStorage.setItem(logoKey, logoData);
    localStorage.setItem(expirationKey, expirationDate.toISOString());

    console.log(`Cached logo for client: ${clientID}, expires: ${expirationDate.toISOString()}`);
  } catch (error) {
    console.error('Error storing cached client logo:', error);
  }
};

/**
 * Clear cached client logo
 */
export const clearClientLogoCache = (clientID: string): void => {
  try {
    const logoKey = getClientLogoKey(clientID);
    const expirationKey = getClientLogoExpirationKey(clientID);

    localStorage.removeItem(logoKey);
    localStorage.removeItem(expirationKey);

    console.log(`Cleared logo cache for client: ${clientID}`);
  } catch (error) {
    console.error('Error clearing client logo cache:', error);
  }
};

/**
 * Clear all cached client logos
 */
export const clearAllClientLogosCache = (): void => {
  try {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith(CLIENT_LOGO_STORAGE_KEY_PREFIX) || key.startsWith(CLIENT_LOGO_EXPIRATION_KEY_PREFIX))) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log(`Cleared ${keysToRemove.length} client logo cache entries`);
  } catch (error) {
    console.error('Error clearing all client logos cache:', error);
  }
};

/**
 * Fetch client logo from the API
 * @param clientID - The client database name
 * @param returnType - 'Base64' or 'Image' (default: 'Base64')
 * @returns The logo data or null if fetch fails
 */
export const fetchClientLogo = async (
  clientID: string,
  returnType: 'Base64' | 'Image' = 'Base64'
): Promise<string | null> => {
  try {
    // Check cache first
    const cachedLogo = getCachedClientLogo(clientID);
    if (cachedLogo) {
      return cachedLogo;
    }

    const apiBaseUrl = import.meta.env.VITE_API_SHARED_URL;

    if (!apiBaseUrl) {
      console.error('VITE_API_SHARED_URL is not configured in environment variables');
      return null;
    }

    // Construct the API URL with query parameters
    const url = new URL(`${apiBaseUrl}api/Blob/GetClientLogo`);
    url.searchParams.append('clientID', clientID);
    url.searchParams.append('returnType', returnType);

    console.log(`Fetching client logo from: ${url.toString()}`);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Accept': returnType === 'Base64' ? 'text/plain' : 'image/*',
      },
      mode: 'cors',
      credentials: 'omit',
      signal: AbortSignal.timeout(30000), // 30 second timeout
    });

    if (!response.ok) {
      if (response.status === 404) {
        // 404 means no logo exists for this client - this is normal, not an error
        console.log(`No logo found for client: ${clientID} (404 - Not Found)`);
        return null;
      }
      
      const errorText = await response.text();
      console.error(`Failed to fetch client logo. Status: ${response.status}, Body: ${errorText}`);
      return null;
    }

    let logoData: string;

    if (returnType === 'Base64') {
      // For Base64, response should be text
      logoData = await response.text();
    } else {
      // For Image, convert to Base64 for storage
      const blob = await response.blob();
      logoData = await blobToBase64(blob);
    }

    if (logoData) {
      // Store in cache
      storeCachedClientLogo(clientID, logoData);
      console.log(`Successfully fetched and cached logo for client: ${clientID}`);
      return logoData;
    }

    return null;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error('Client logo fetch timeout:', error);
    } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      console.error('Network error fetching client logo:', error);
    } else {
      console.error('Error fetching client logo:', error);
    }
    return null;
  }
};

/**
 * Convert Blob to Base64 string
 */
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Remove data URL prefix if present (e.g., "data:image/png;base64,")
      const base64 = result.split(',')[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

